"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Instagram, Check, Loader2 } from "lucide-react"

interface Props {
  slug: string
  alreadyPosted: boolean
}

export function InstagramPublishButton({ slug, alreadyPosted }: Props) {
  const [status, setStatus] = useState<"idle" | "confirming" | "publishing" | "done" | "error">(
    alreadyPosted ? "done" : "idle"
  )
  const [error, setError] = useState<string | null>(null)

  async function publish() {
    setStatus("publishing")
    setError(null)

    try {
      const res = await fetch(`/api/articles/${slug}/instagram`, { method: "POST" })
      const data = await res.json()

      if (!res.ok) {
        setError(data.error ?? "Failed to publish")
        setStatus(res.status === 409 ? "done" : "error")
        return
      }

      setStatus("done")
    } catch {
      setError("Network error — try again")
      setStatus("error")
    }
  }

  if (status === "done") {
    return (
      <Button size="sm" variant="outline" disabled className="gap-1.5 text-[var(--accent)]">
        <Check size={14} />
        Posted to Instagram
      </Button>
    )
  }

  if (status === "confirming") {
    return (
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={publish} className="gap-1.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white border-0">
          <Instagram size={14} />
          Confirm post
        </Button>
        <Button size="sm" variant="outline" onClick={() => setStatus("idle")}>
          Cancel
        </Button>
      </div>
    )
  }

  if (status === "publishing") {
    return (
      <Button size="sm" variant="outline" disabled className="gap-1.5">
        <Loader2 size={14} className="animate-spin" />
        Publishing…
      </Button>
    )
  }

  return (
    <div className="space-y-1">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setStatus("confirming")}
        className="gap-1.5"
      >
        <Instagram size={14} />
        Post to Instagram
      </Button>
      {status === "error" && error && (
        <p className="text-xs text-red-600">{error}</p>
      )}
    </div>
  )
}
